const BREADCRUMB_PENDING_KEY = 'seacase:breadcrumb-pending:v1';
const BREADCRUMB_STATE_KEY = 'seacaseBreadcrumbParent';
const BREADCRUMB_LEGACY_KEY = 'seacase:breadcrumb-history:v1';
const BREADCRUMB_PENDING_TTL = 10 * 60 * 1000;

const breadcrumbScript = document.currentScript;
const breadcrumbPage = {
  title: breadcrumbScript?.dataset.pageTitle?.trim() || document.title.trim(),
  type: breadcrumbScript?.dataset.pageType || '',
  templateSuffix: breadcrumbScript?.dataset.templateSuffix || '',
  isEligibleParent: breadcrumbScript?.dataset.parentEligible === 'true',
};
const nativeReplaceState = window.history.replaceState.bind(window.history);

function normalizePath(path) {
  const normalized = path?.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/';
  return normalized || '/';
}

function isProductPath(path) {
  return /\/products\/[^/]+$/i.test(normalizePath(path));
}

function safeRelativeUrl(value) {
  if (typeof value !== 'string' || !value) return null;

  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

function isValidParent(parent) {
  if (!parent || typeof parent !== 'object') return false;
  if (!['collection', 'design-page'].includes(parent.kind)) return false;
  if (typeof parent.title !== 'string' || !parent.title.trim() || parent.title.length > 160) return false;

  const parentUrl = safeRelativeUrl(parent.url);
  if (!parentUrl) return false;

  return !isProductPath(new URL(parentUrl, window.location.origin).pathname);
}

function stateObject(state) {
  return state && typeof state === 'object' && !Array.isArray(state) ? state : {};
}

function parentFromState(state = window.history.state) {
  const parent = stateObject(state)[BREADCRUMB_STATE_KEY];
  return isValidParent(parent) ? parent : null;
}

function currentParentContext() {
  if (!breadcrumbPage.isEligibleParent || !breadcrumbPage.title) return null;

  const url = safeRelativeUrl(window.location.href);
  if (!url) return null;

  return {
    kind: breadcrumbPage.type === 'collection' ? 'collection' : 'design-page',
    title: breadcrumbPage.title.slice(0, 160),
    url,
  };
}

function readPendingContexts() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(BREADCRUMB_PENDING_KEY) || '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const now = Date.now();
    return Object.fromEntries(
      Object.entries(parsed).filter(([, pending]) => {
        return (
          pending &&
          Number.isFinite(pending.createdAt) &&
          now - pending.createdAt <= BREADCRUMB_PENDING_TTL &&
          isValidParent(pending.parent)
        );
      })
    );
  } catch {
    return {};
  }
}

function writePendingContexts(contexts) {
  try {
    if (Object.keys(contexts).length === 0) {
      sessionStorage.removeItem(BREADCRUMB_PENDING_KEY);
      return;
    }

    sessionStorage.setItem(BREADCRUMB_PENDING_KEY, JSON.stringify(contexts));
  } catch {
    // Storage can be unavailable in privacy modes. The server-rendered fallback remains usable.
  }
}

function storePendingContext(targetPath, parent) {
  if (!isProductPath(targetPath) || !isValidParent(parent)) return;

  const contexts = readPendingContexts();
  contexts[normalizePath(targetPath)] = {
    createdAt: Date.now(),
    parent,
  };
  writePendingContexts(contexts);
}

function consumePendingContext(targetPath) {
  const contexts = readPendingContexts();
  const key = normalizePath(targetPath);
  const pending = contexts[key];
  delete contexts[key];
  writePendingContexts(contexts);
  return pending && isValidParent(pending.parent) ? pending.parent : null;
}

function resolveProductParent() {
  if (breadcrumbPage.type !== 'product') return null;

  const stateParent = parentFromState();
  if (stateParent) return stateParent;

  const pendingParent = consumePendingContext(window.location.pathname);
  if (!pendingParent) return null;

  nativeReplaceState(
    {
      ...stateObject(window.history.state),
      [BREADCRUMB_STATE_KEY]: pendingParent,
    },
    '',
    window.location.href
  );
  return pendingParent;
}

function preserveProductParentOnReplaceState() {
  window.history.replaceState = function breadcrumbReplaceState(state, unused, url) {
    const parent = parentFromState();
    const nextState = parent
      ? {
          ...stateObject(state),
          [BREADCRUMB_STATE_KEY]: parent,
        }
      : state;

    return nativeReplaceState(nextState, unused, url);
  };
}

function navigationParent() {
  if (breadcrumbPage.isEligibleParent) return currentParentContext();
  if (breadcrumbPage.type === 'product') return parentFromState();
  return null;
}

function productLinkFromClickTarget(target) {
  const directLink = target?.closest('a[href]');
  if (directLink) return directLink;

  const productCard = target?.closest('product-card');
  if (!productCard || productCard.hasAttribute('data-no-navigation')) return null;
  if (target.closest('button, input, label, select, [tabindex="1"]')) return null;

  return productCard.querySelector('a[ref="productCardLink"][href], a.product-card__link[href]');
}

function handleProductLinkClick(event) {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const target = event.target instanceof Element ? event.target : event.target?.parentElement;
  const link = productLinkFromClickTarget(target);
  if (!link || link.hasAttribute('download')) return;
  if (link.target && link.target.toLowerCase() !== '_self') return;

  let targetUrl;
  try {
    targetUrl = new URL(link.href, window.location.origin);
  } catch {
    return;
  }

  if (targetUrl.origin !== window.location.origin || !isProductPath(targetUrl.pathname)) return;

  const parent = navigationParent();
  if (parent) storePendingContext(targetUrl.pathname, parent);
}

function createSeparator(separator) {
  const item = document.createElement('li');
  item.className = 'breadcrumb__separator';
  item.setAttribute('aria-hidden', 'true');
  item.textContent = separator;
  item.dataset.breadcrumbGenerated = '';
  return item;
}

function createParentItem(parent) {
  const item = document.createElement('li');
  item.className = 'breadcrumb__item breadcrumb__item--context';
  item.dataset.breadcrumbGenerated = '';

  const link = document.createElement('a');
  link.className = 'breadcrumb__link';
  link.href = parent.url;
  link.textContent = parent.title;

  item.append(link);
  return item;
}

function renderBreadcrumb(breadcrumb, parent) {
  breadcrumb.querySelectorAll('[data-breadcrumb-generated]').forEach((item) => item.remove());
  if (
    breadcrumbPage.type !== 'product' ||
    breadcrumb.dataset.breadcrumbVariant !== 'product' ||
    !isValidParent(parent)
  ) {
    return;
  }

  const baseSeparator = breadcrumb.querySelector('[data-breadcrumb-base-separator]');
  if (!baseSeparator) return;

  const separator = breadcrumb.dataset.separator || '›';
  baseSeparator.before(createSeparator(separator), createParentItem(parent));
}

function syncBreadcrumbs() {
  const parent = resolveProductParent();
  document.querySelectorAll('[data-breadcrumb]').forEach((breadcrumb) => {
    renderBreadcrumb(breadcrumb, parent);
  });
}

try {
  sessionStorage.removeItem(BREADCRUMB_LEGACY_KEY);
} catch {
  // Ignore unavailable storage.
}

preserveProductParentOnReplaceState();
document.addEventListener('click', handleProductLinkClick, true);
window.addEventListener('popstate', syncBreadcrumbs);
document.addEventListener('shopify:section:load', syncBreadcrumbs);
syncBreadcrumbs();
